import React, { useMemo } from 'react';
import { Col, Input, Row, Select } from 'antd';
import { useIntl } from 'react-intl';

import { FormField } from '../form-field';
import type { PaymentFormValues } from '../../types';
import { usePaymentFormValidationRules } from '../../payment-form-validation-context';
import { MAX_LENGTH_ADDRESS, MAX_LENGTH_POSTAL, COUNTRY_OPTIONS } from '../../constants';

import styles from './styles.module.scss';

export type BillingAddressFieldsVariant = 'default' | 'billing1' | 'billing2';

const FIELD_NAMES: Record<
  BillingAddressFieldsVariant,
  {
    address: keyof PaymentFormValues;
    city: keyof PaymentFormValues;
    country: keyof PaymentFormValues;
    postal: keyof PaymentFormValues;
  }
> = {
  default: {
    address: 'addressLine',
    city: 'city',
    country: 'country',
    postal: 'postalCode',
  },
  billing1: {
    address: 'addressLine',
    city: 'city',
    country: 'country',
    postal: 'postalCode',
  },
  billing2: {
    address: 'addressLine2',
    city: 'city2',
    country: 'country2',
    postal: 'postalCode2',
  },
};

export interface BillingAddressFieldsProps {
  formItemClassName: string;
  /** default: single billing block; billing1/billing2: pay-in-full / instalment split labels and field sets. */
  variant?: BillingAddressFieldsVariant;
}

/**
 * Billing layout matches Figma: full-width Address, City, Country; Postal Code ~⅓ width, left-aligned.
 */
export function BillingAddressFields({
  formItemClassName,
  variant = 'default',
}: BillingAddressFieldsProps): React.ReactElement {
  const intl = useIntl();
  const validationRules = usePaymentFormValidationRules();

  const sectionTitle = useMemo(() => {
    if (variant === 'billing1') {
      return intl.formatMessage({ id: 'certificates.payment.billingAddress1' });
    }
    if (variant === 'billing2') {
      return intl.formatMessage({ id: 'certificates.payment.billingAddress2' });
    }
    return intl.formatMessage({ id: 'certificates.payment.billingAddress' });
  }, [intl, variant]);

  const n = FIELD_NAMES[variant];

  return (
    <>
      <p className={styles.subsectionLabel}>{sectionTitle}:</p>
      <FormField
        name={n.address}
        label={intl.formatMessage({ id: 'certificates.payment.address' })}
        variant="input"
        rules={validationRules.addressLine}
        className={formItemClassName}
      >
        <Input
          placeholder={intl.formatMessage({ id: 'certificates.payment.placeholder.addressLine' })}
          maxLength={MAX_LENGTH_ADDRESS}
        />
      </FormField>
      <FormField
        name={n.city}
        label={intl.formatMessage({ id: 'certificates.payment.city' })}
        variant="input"
        rules={validationRules.city}
        className={formItemClassName}
      >
        <Input
          placeholder={intl.formatMessage({ id: 'certificates.payment.city' })}
          maxLength={MAX_LENGTH_ADDRESS}
        />
      </FormField>
      <FormField
        name={n.country}
        label={intl.formatMessage({ id: 'certificates.payment.country' })}
        variant="select"
        rules={validationRules.country}
        className={formItemClassName}
      >
        <Select
          options={COUNTRY_OPTIONS}
          optionFilterProp="label"
          placeholder={intl.formatMessage({ id: 'certificates.payment.placeholder.country' })}
          getPopupContainer={(el) => el.parentElement ?? document.body}
        />
      </FormField>
      <Row gutter={10}>
        <Col xs={24} sm={8} md={7}>
          <FormField
            name={n.postal}
            label={intl.formatMessage({ id: 'certificates.payment.postalCode' })}
            variant="input"
            rules={validationRules.postalCode}
            className={formItemClassName}
          >
            <Input
              placeholder={intl.formatMessage({ id: 'certificates.payment.placeholder.postcode' })}
              maxLength={MAX_LENGTH_POSTAL}
            />
          </FormField>
        </Col>
      </Row>
    </>
  );
}
